import React from "react";
import Pill from "../zest/Pill";
import type { IconLibrary, PillColor } from "../zest/types";

export interface FilterOption {
  value: string;
  label: string;
  icon?: string | null;
  iconLibrary?: IconLibrary;
  color?: PillColor;
  colorClasses?: string | null;
}

export interface FilterGroup {
  key: string;
  label: string;
  selected: string[];
  options: FilterOption[];
}

export interface FiltersProps {
  groups: FilterGroup[];
  onToggle: (groupKey: string, value: string) => void;
  onClear: (groupKey: string) => void;
}

interface PillButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  pressed: boolean;
}

const PillButton = ({ onClick, children, pressed }: PillButtonProps): JSX.Element => (
  <button type="button" aria-pressed={pressed} onClick={onClick}>
    {children}
  </button>
);

interface FilterRowProps {
  label: string;
  anySelected: boolean;
  onClear: () => void;
  children: React.ReactNode;
}

const FilterRow = ({ label, anySelected, onClear, children }: FilterRowProps): JSX.Element => (
  <div className="flex flex-wrap items-center gap-2 py-1">
    <span className="font-bold text-secondary">{label}</span>
    <PillButton onClick={onClear} pressed={!anySelected}>
      <Pill text="All" icon="filter" outlined={anySelected} />
    </PillButton>
    {children}
  </div>
);

const Filters = ({ groups, onToggle, onClear }: FiltersProps): JSX.Element => (
  <div>
    {groups.map((group) => (
      <FilterRow
        key={group.key}
        label={group.label}
        anySelected={group.selected.length > 0}
        onClear={() => onClear(group.key)}
      >
        {group.options.map((option) => {
          const selected = group.selected.includes(option.value);
          return (
            <PillButton key={option.value} pressed={selected} onClick={() => onToggle(group.key, option.value)}>
              <Pill
                text={option.label}
                icon={option.icon}
                iconLibrary={option.iconLibrary}
                outlined={!selected}
                color={option.color}
                colorClasses={option.colorClasses}
              />
            </PillButton>
          );
        })}
      </FilterRow>
    ))}
  </div>
);

export default Filters;
